/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — FormSection layout component
 */
import cx from 'classnames';
import * as React from 'react';

import type { LayoutSpacing } from '../../types';

import './FormSection.less';

export interface FormSectionProps {
  /** Section heading rendered as an <h3> */
  title?: React.ReactNode;
  /**
   * Controls the visual size of the title.
   * - 'large'  (default) — h3 heading size
   * - 'medium' — body-1 size, semibold
   * - 'small'  — body-2 size, semibold
   */
  titleSize?: 'large' | 'medium' | 'small';
  /** Optional subtitle rendered below the title */
  description?: React.ReactNode;
  /**
   * Show a 1px bottom-border separator.
   * Automatically hidden on the last child via CSS :last-child.
   * Default: true
   */
  divider?: boolean;
  /**
   * Gap between children from the DS spacing scale.
   * Default: 2 (8px)
   */
  gap?: LayoutSpacing;
  className?: string;
  children?: React.ReactNode;
}

const FormSection: React.FC<FormSectionProps> = ({
  title,
  titleSize = 'large',
  description,
  divider = true,
  gap = 2,
  className,
  children,
}) => (
  <div className={cx('ds-form-section', divider && 'ds-form-section--divider', className)}>
    {(title || description) && (
      <div className="ds-form-section__header">
        {title && (
          <h3 className={cx('ds-form-section__title', titleSize !== 'large' && `ds-form-section__title--${titleSize}`)}>
            {title}
          </h3>
        )}
        {description && <p className="ds-form-section__description">{description}</p>}
      </div>
    )}
    <div className={cx('ds-form-section__content', gap !== 0 && `ds-form-section__content--gap-${gap}`)}>
      {children}
    </div>
  </div>
);

FormSection.displayName = 'FormSection';

export { FormSection };
export default FormSection;
